/**
 * QuestTemplates.mjs
 * Template library for Pattern Quest v2.
 *
 * Each template:
 *   id, tier, category, progressKind
 *   bind(worldInfo) → { params, title, description, signature, reward, progress } | null
 */

// ─── helpers ─────────────────────────────────────────────────────────────────

function scaleTarget(totalTiles, [min, max]) {
  let fraction = 0;
  if (totalTiles >= 30 && totalTiles < 120) fraction = (totalTiles - 30) / 90;
  else if (totalTiles >= 120) fraction = 1;
  return Math.round(min + (max - min) * fraction);
}

function pickRand(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function pickHandFriendlyTerrain(worldInfo) {
  const { handTerrainTypes, exposedTopTypes, boardTerrainCounts } = worldInfo;
  const r = Math.random();
  if (r < 0.6 && exposedTopTypes.length > 0) {
    for (const t of exposedTopTypes) {
      if (handTerrainTypes.has(t)) return t;
    }
    return exposedTopTypes[0];
  }
  if (r < 0.9) {
    const boardTypes = [...boardTerrainCounts.keys()];
    if (boardTypes.length > 0) return pickRand(boardTypes);
  }
  if (handTerrainTypes.size > 0) return pickRand([...handTerrainTypes]);
  return 'grass';
}

const TERRAIN_NAMES = {
  ocean: 'ocean', grass: 'grass', grassland: 'grassland',
  mountain: 'mountain', snow: 'snow', desert: 'desert', deadplains: 'dead plains',
};
function tname(t) { return TERRAIN_NAMES[t] || t; }

function starsReward(n)          { return { kind: 'stars', amount: n }; }
function regularOverlayReward()  { return { kind: 'overlay', pool: 'regular', fallbackStars: 75 }; }
function specialOverlayReward()  { return { kind: 'overlay', pool: 'special', fallbackStars: 100 }; }

function creativeReward() {
  return Math.random() < 0.4 ? regularOverlayReward() : starsReward(75);
}

function count(target) { return { kind: 'count', current: 0, target }; }
function best(target)  { return { kind: 'bestSoFar', bestSoFar: 0, target }; }
function bool()        { return { kind: 'boolean', done: false }; }

// ─── Template list ────────────────────────────────────────────────────────────

/** @type {Array<{id:string, tier:string, category:string, bind:Function}>} */
export const QUEST_TEMPLATES = [

  // ══════════════════════════════════════════════════════════════════
  // LIGHT (reward: +50 ⭐)
  // ══════════════════════════════════════════════════════════════════

  {
    id: 'L1', tier: 'light', category: 'expand_world',
    bind(wi) {
      const target = scaleTarget(wi.totalTiles, [6, 12]);
      return {
        params: { target },
        title: 'Soft steps',
        description: `Place ${target} tiles.`,
        signature: `expand_world:any:${target}`,
        reward: starsReward(50),
        progress: count(target),
      };
    },
  },

  {
    id: 'L2', tier: 'light', category: 'place_tiles',
    bind(wi) {
      if (wi.boardTerrainCounts.size === 0) return null;
      const terrain = pickHandFriendlyTerrain(wi);
      const target = scaleTarget(wi.totalTiles, [6, 10]);
      return {
        params: { terrain, target },
        title: `A touch of ${tname(terrain)}`,
        description: `Place ${target} ${tname(terrain)} tiles.`,
        signature: `place_tiles:${terrain}:${target}`,
        reward: starsReward(50),
        progress: count(target),
      };
    },
  },

  {
    id: 'L3', tier: 'light', category: 'match_edges',
    bind(wi) {
      const target = scaleTarget(wi.totalTiles, [4, 9]);
      return {
        params: { target },
        title: 'New edges, new paths',
        description: `Create ${target} matching edges.`,
        signature: `match_edges:${target}`,
        reward: starsReward(50),
        progress: count(target),
      };
    },
  },

  {
    id: 'L5', tier: 'light', category: 'place_overlays',
    bind(wi) {
      if (wi.unlockedFamilies.size === 0) return null;
      const target = scaleTarget(wi.totalTiles, [2, 6]);
      return {
        params: { target },
        title: 'Place a little life',
        description: `Place ${target} overlays.`,
        signature: `place_overlays:${target}`,
        reward: starsReward(50),
        progress: count(target),
      };
    },
  },

  {
    id: 'L7', tier: 'light', category: 'place_tiles',
    bind(wi) {
      if (wi.boardTerrainCounts.size < 2) return null;
      const kinds = Math.min(wi.boardTerrainCounts.size, 3);
      return {
        params: { kinds },
        title: 'A gentle mix',
        description: `Place tiles from ${kinds} different terrains.`,
        signature: `multi_terrain:${kinds}`,
        reward: starsReward(50),
        progress: { kind: 'distinct', values: [], target: kinds },
      };
    },
  },

  {
    id: 'L8', tier: 'light', category: 'place_tiles',
    bind(wi) {
      if (!wi.boardTerrainCounts.has('ocean') && !wi.handTerrainTypes.has('ocean')) return null;
      const target = scaleTarget(wi.totalTiles, [4, 8]);
      return {
        params: { terrain: 'ocean', target },
        title: 'Calm waters',
        description: `Place ${target} ocean tiles.`,
        signature: `place_tiles:ocean:${target}`,
        reward: starsReward(50),
        progress: count(target),
      };
    },
  },

  {
    id: 'L9', tier: 'light', category: 'place_tiles',
    bind(wi) {
      if (!wi.boardTerrainCounts.has('desert') && !wi.handTerrainTypes.has('desert')) return null;
      const target = scaleTarget(wi.totalTiles, [4, 8]);
      return {
        params: { terrain: 'desert', target },
        title: 'Warm sands',
        description: `Place ${target} desert tiles.`,
        signature: `place_tiles:desert:${target}`,
        reward: starsReward(50),
        progress: count(target),
      };
    },
  },

  {
    id: 'L10', tier: 'light', category: 'place_tiles',
    bind(wi) {
      if (!wi.boardTerrainCounts.has('snow') && !wi.handTerrainTypes.has('snow')) return null;
      const target = scaleTarget(wi.totalTiles, [4, 8]);
      return {
        params: { terrain: 'snow', target },
        title: 'Snowdust',
        description: `Place ${target} snow tiles.`,
        signature: `place_tiles:snow:${target}`,
        reward: starsReward(50),
        progress: count(target),
      };
    },
  },

  {
    id: 'L11', tier: 'light', category: 'place_tiles',
    bind(wi) {
      if (!wi.boardTerrainCounts.has('mountain') && !wi.handTerrainTypes.has('mountain')) return null;
      const target = scaleTarget(wi.totalTiles, [4, 8]);
      return {
        params: { terrain: 'mountain', target },
        title: 'Stone and sky',
        description: `Place ${target} mountain tiles.`,
        signature: `place_tiles:mountain:${target}`,
        reward: starsReward(50),
        progress: count(target),
      };
    },
  },

  {
    id: 'L12', tier: 'light', category: 'place_tiles',
    bind(wi) {
      const hasGrass = wi.boardTerrainCounts.has('grass') || wi.handTerrainTypes.has('grass');
      const hasGL = wi.boardTerrainCounts.has('grassland') || wi.handTerrainTypes.has('grassland');
      if (!hasGrass && !hasGL) return null;
      const target = scaleTarget(wi.totalTiles, [6, 10]);
      return {
        params: { terrainSet: ['grass', 'grassland'], target },
        title: 'Meadow days',
        description: `Place ${target} grass or grassland tiles.`,
        signature: `place_tiles:grass_grassland:${target}`,
        reward: starsReward(50),
        progress: count(target),
      };
    },
  },

  {
    id: 'L13', tier: 'light', category: 'make_cluster',
    bind(wi) {
      const target = scaleTarget(wi.totalTiles, [4, 6]);
      return {
        params: { terrain: null, target },
        title: 'A little cluster',
        description: `Build a cluster of ${target} same-terrain tiles.`,
        signature: `cluster:any:${target}`,
        reward: starsReward(50),
        progress: best(target),
      };
    },
  },

  {
    id: 'L14', tier: 'light', category: 'expand_world',
    bind(wi) {
      if (wi.totalTiles < 4) return null;
      const target = scaleTarget(wi.totalTiles, [2, 4]);
      return {
        params: { minNeighbours: 2, target },
        title: 'Two neighbours',
        description: `Place ${target} tiles that each touch 2+ existing tiles.`,
        signature: `neighbours:2:${target}`,
        reward: starsReward(50),
        progress: count(target),
      };
    },
  },

  {
    id: 'L15', tier: 'light', category: 'overlay_adjacency',
    bind(wi) {
      if (wi.unlockedFamilies.size === 0) return null;
      if (wi.boardOverlayCount === 0) return null;
      const target = scaleTarget(wi.totalTiles, [2, 5]);
      return {
        params: { minNeighbourOverlays: 1, target },
        title: 'Tiny sparkles',
        description: `Place ${target} overlays next to another overlay.`,
        signature: `overlay_adj:1:${target}`,
        reward: starsReward(50),
        progress: count(target),
      };
    },
  },

  // ══════════════════════════════════════════════════════════════════
  // CREATIVE (reward: +75 ⭐ or regular overlay)
  // ══════════════════════════════════════════════════════════════════

  {
    id: 'C1', tier: 'creative', category: 'make_cluster',
    bind(wi) {
      if (wi.boardTerrainCounts.size === 0) return null;
      const terrain = pickHandFriendlyTerrain(wi);
      const target = scaleTarget(wi.totalTiles, [5, 8]);
      return {
        params: { terrain, target },
        title: `Patch of ${tname(terrain)}`,
        description: `Grow a ${tname(terrain)} patch to ${target} connected tiles.`,
        signature: `cluster:${terrain}:${target}`,
        reward: creativeReward(),
        progress: best(target),
      };
    },
  },

  {
    id: 'C3', tier: 'creative', category: 'place_tiles',
    bind(wi) {
      if (wi.totalTiles < 5) return null;
      const target = scaleTarget(wi.totalTiles, [3, 6]);
      return {
        params: { minTerrainVariety: 2, target },
        title: 'Gentle gradient',
        description: `Place ${target} tiles where each touches 2+ different terrains.`,
        signature: `gradient:${target}`,
        reward: creativeReward(),
        progress: count(target),
      };
    },
  },

  {
    id: 'C4', tier: 'creative', category: 'make_chain',
    bind(wi) {
      if (wi.boardTerrainCounts.size === 0) return null;
      const terrain = pickHandFriendlyTerrain(wi);
      const target = scaleTarget(wi.totalTiles, [4, 7]);
      return {
        params: { terrain, target },
        title: 'A quiet line',
        description: `Make a straight line of ${target} ${tname(terrain)} tiles.`,
        signature: `line:${terrain}:${target}`,
        reward: creativeReward(),
        progress: best(target),
      };
    },
  },

  {
    id: 'C5', tier: 'creative', category: 'make_chain',
    bind(wi) {
      if (wi.totalTiles < 6) return null;
      const target = scaleTarget(wi.totalTiles, [4, 7]);
      return {
        params: { terrain: null, target },
        title: 'Winding path',
        description: `Make a straight line of ${target} same-terrain tiles.`,
        signature: `line:any:${target}`,
        reward: creativeReward(),
        progress: best(target),
      };
    },
  },

  {
    id: 'C6', tier: 'creative', category: 'make_coastline',
    bind(wi) {
      const hasOcean = wi.boardTerrainCounts.has('ocean') || wi.handTerrainTypes.has('ocean');
      if (!hasOcean) return null;
      const target = scaleTarget(wi.totalTiles, [4, 8]);
      return {
        params: { target },
        title: 'A small bay',
        description: `Shape a coastline with ${target} ocean-to-land edges.`,
        signature: `coastline:${target}`,
        reward: creativeReward(),
        progress: best(target),
      };
    },
  },

  {
    id: 'C8', tier: 'creative', category: 'make_ring',
    bind(wi) {
      if (wi.totalTiles < 8) return null;
      return {
        params: { target: 1 },
        title: 'Surround a quiet heart',
        description: 'Surround one empty hex with tiles.',
        signature: 'ring:1',
        reward: creativeReward(),
        progress: bool(),
      };
    },
  },

  {
    id: 'C9', tier: 'creative', category: 'make_ring',
    bind(wi) {
      if (wi.totalTiles < 20) return null;
      return {
        params: { target: 2 },
        title: 'Two quiet hearts',
        description: 'Surround two empty hexes with tiles.',
        signature: 'ring:2',
        reward: creativeReward(),
        progress: count(2),
      };
    },
  },

  {
    id: 'C10', tier: 'creative', category: 'overlay_adjacency',
    bind(wi) {
      if (wi.boardOverlayCount < 2) return null;
      if (wi.unlockedFamilies.size === 0) return null;
      const target = scaleTarget(wi.totalTiles, [1, 3]);
      return {
        params: { minNeighbourOverlays: 2, target },
        title: 'Neighbourhood bloom',
        description: `Place an overlay with 2+ neighbouring overlays, ${target} time${target > 1 ? 's' : ''}.`,
        signature: `overlay_adj:2:${target}`,
        reward: creativeReward(),
        progress: count(target),
      };
    },
  },

  {
    id: 'C11', tier: 'creative', category: 'overlay_cluster',
    bind(wi) {
      if (!wi.unlockedFamilies.has('forest')) return null;
      const target = scaleTarget(wi.totalTiles, [3, 6]);
      return {
        params: { family: 'forest', target },
        title: 'A cozy grove',
        description: `Build a connected cluster of ${target} forest overlays.`,
        signature: `overlay_cluster:forest:${target}`,
        reward: creativeReward(),
        progress: best(target),
      };
    },
  },

  {
    id: 'C12', tier: 'creative', category: 'overlay_cluster',
    bind(wi) {
      if (!wi.unlockedFamilies.has('mountainrocks')) return null;
      const target = scaleTarget(wi.totalTiles, [3, 6]);
      return {
        params: { family: 'mountainrocks', target },
        title: 'Little rock garden',
        description: `Build a connected cluster of ${target} mountain rock overlays.`,
        signature: `overlay_cluster:mountainrocks:${target}`,
        reward: creativeReward(),
        progress: best(target),
      };
    },
  },

  {
    id: 'C13', tier: 'creative', category: 'place_overlays',
    bind(wi) {
      const has = wi.unlockedFamilies.has('island_land') || wi.unlockedFamilies.has('island_water');
      if (!has) return null;
      const target = scaleTarget(wi.totalTiles, [2, 5]);
      return {
        params: { families: ['island_land', 'island_water'], target },
        title: 'Isles together',
        description: `Place ${target} island overlays.`,
        signature: `place_overlay_family:island:${target}`,
        reward: creativeReward(),
        progress: count(target),
      };
    },
  },

  {
    id: 'C14', tier: 'creative', category: 'match_edges',
    bind(wi) {
      if (wi.totalTiles < 5) return null;
      const target = scaleTarget(wi.totalTiles, [2, 5]);
      return {
        params: { minMatches: 3, target },
        title: 'Pattern of three',
        description: `Place ${target} tiles each creating 3+ matching edges.`,
        signature: `match_edges:3plus:${target}`,
        reward: creativeReward(),
        progress: count(target),
      };
    },
  },

  {
    id: 'C15', tier: 'creative', category: 'match_edges',
    bind(wi) {
      if (wi.totalTiles < 8) return null;
      return {
        params: { minMatches: 4 },
        title: 'Gentle six',
        description: 'Place one tile that creates 4+ matching edges.',
        signature: 'match_edges:4:1',
        reward: creativeReward(),
        progress: bool(),
      };
    },
  },

  // ══════════════════════════════════════════════════════════════════
  // SPECIAL (reward: special overlay, fallback +100 ⭐)
  // ══════════════════════════════════════════════════════════════════

  {
    id: 'S1', tier: 'special', category: 'place_overlays',
    bind(wi) {
      if (wi.totalTiles < 15) return null;
      if (wi.unlockedFamilies.size === 0) return null;
      if (wi.score.stars < 50 && wi.totalTiles < 25) return null;
      const target = scaleTarget(wi.totalTiles, [2, 5]);
      return {
        params: { bonusTerrain: true, target },
        title: 'A place for company',
        description: `Place ${target} overlays on their favourite terrain.`,
        signature: `bonus_terrain:${target}`,
        reward: specialOverlayReward(),
        progress: count(target),
      };
    },
  },

  {
    id: 'S2', tier: 'special', category: 'overlay_adjacency',
    bind(wi) {
      if (wi.boardOverlayCount < 4) return null;
      if (wi.unlockedFamilies.size === 0) return null;
      if (wi.score.stars < 50 && wi.totalTiles < 25) return null;
      return {
        params: { minNeighbourOverlays: 4 },
        title: 'Lively neighbourhood',
        description: 'Place an overlay that touches 4 other overlays.',
        signature: 'overlay_adj:4:1',
        reward: specialOverlayReward(),
        progress: bool(),
      };
    },
  },

  {
    id: 'S3', tier: 'special', category: 'overlay_cluster',
    bind(wi) {
      if (!wi.unlockedFamilies.has('forest')) return null;
      if (wi.score.stars < 50 && wi.totalTiles < 25) return null;
      const target = scaleTarget(wi.totalTiles, [6, 10]);
      return {
        params: { family: 'forest', target },
        title: 'Grand grove',
        description: `Grow a forest overlay cluster to ${target}.`,
        signature: `overlay_cluster:forest:${target}`,
        reward: specialOverlayReward(),
        progress: best(target),
      };
    },
  },

  {
    id: 'S4', tier: 'special', category: 'make_cluster',
    bind(wi) {
      if (!wi.boardTerrainCounts.has('mountain') && !wi.handTerrainTypes.has('mountain')) return null;
      if (wi.totalTiles < 20) return null;
      if (wi.score.stars < 50 && wi.totalTiles < 25) return null;
      const target = scaleTarget(wi.totalTiles, [8, 12]);
      return {
        params: { terrain: 'mountain', target },
        title: 'Mountain crown',
        description: `Make a mountain cluster of ${target} connected tiles.`,
        signature: `cluster:mountain:${target}`,
        reward: specialOverlayReward(),
        progress: best(target),
      };
    },
  },

  {
    id: 'S5', tier: 'special', category: 'make_cluster',
    bind(wi) {
      if (!wi.boardTerrainCounts.has('snow') && !wi.handTerrainTypes.has('snow')) return null;
      if (wi.totalTiles < 20) return null;
      if (wi.score.stars < 50 && wi.totalTiles < 25) return null;
      const target = scaleTarget(wi.totalTiles, [8, 12]);
      return {
        params: { terrain: 'snow', target },
        title: 'Snowcap plateau',
        description: `Make a snow cluster of ${target} connected tiles.`,
        signature: `cluster:snow:${target}`,
        reward: specialOverlayReward(),
        progress: best(target),
      };
    },
  },

  {
    id: 'S6', tier: 'special', category: 'make_cluster',
    bind(wi) {
      if (!wi.boardTerrainCounts.has('desert') && !wi.handTerrainTypes.has('desert')) return null;
      if (wi.totalTiles < 20) return null;
      if (wi.score.stars < 50 && wi.totalTiles < 25) return null;
      const target = scaleTarget(wi.totalTiles, [8, 12]);
      return {
        params: { terrain: 'desert', target },
        title: 'Desert bloom',
        description: `Make a desert cluster of ${target} connected tiles.`,
        signature: `cluster:desert:${target}`,
        reward: specialOverlayReward(),
        progress: best(target),
      };
    },
  },

  {
    id: 'S8', tier: 'special', category: 'make_ring',
    bind(wi) {
      if (wi.totalTiles < 25) return null;
      if (wi.score.stars < 50 && wi.totalTiles < 25) return null;
      const target = scaleTarget(wi.totalTiles, [2, 4]);
      return {
        params: { target },
        title: 'Hidden circles',
        description: `Surround ${target} empty hexes with tiles.`,
        signature: `ring:${target}`,
        reward: specialOverlayReward(),
        progress: count(target),
      };
    },
  },

  {
    id: 'S9', tier: 'special', category: 'make_cluster',
    bind(wi) {
      if (wi.boardTerrainCounts.size < 3) return null;
      if (wi.totalTiles < 20) return null;
      if (wi.score.stars < 50 && wi.totalTiles < 25) return null;
      const clusterSize = scaleTarget(wi.totalTiles, [4, 6]);
      const kinds = 3;
      return {
        params: { kinds, clusterSize },
        title: 'Three biomes, one world',
        description: `Build ${kinds} terrain clusters of ${clusterSize}+ tiles each.`,
        signature: `biome_trio:${kinds}:${clusterSize}`,
        reward: specialOverlayReward(),
        progress: { kind: 'biome_trio', achievedTerrains: [], clusterSize, target: kinds },
      };
    },
  },

  // S10 — meta fallback (always feasible for special tier)
  {
    id: 'S10', tier: 'special', category: 'meta',
    bind(wi) {
      const target = scaleTarget(wi.totalTiles, [2, 4]);
      return {
        params: { target },
        title: 'A wonder appears',
        description: `Complete any ${target} quests.`,
        signature: `meta:${target}`,
        reward: specialOverlayReward(),
        progress: count(target),
      };
    },
  },
];
